// Implements: F-MCK-013, F-PRV-015
/**
 * The thumbnail of a gallery card: the v3 generic mockup wireframe (`.th`), drawn with CSS.
 *
 * No image is fetched and nothing renders the mockup (owner decision 2026-10-14): a top bar, a
 * sidebar and a main area with an accent block and three columns, tinted by the project's primary
 * once the mockup has generated and by the neutral tint of the tokens before, with the mockup's
 * name on it. The colour is data, set as `--pc` through `style.setProperty` (the CSP forbids
 * `style=""`); every other colour is a theme alias, so the light and the dark theme both apply.
 * The tile is an image with a name, so a screen reader hears which mockup it pictures; the name is
 * React text, never markup. Pure presentation.
 */

import { useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { MockupSummary } from "../../../types/api";
import styles from "./MockupThumbnail.module.css";
import { thumbnailTemplateOf } from "./thumbnailTemplate";

const COLUMNS = [0, 1, 2] as const;

export interface MockupThumbnailProps {
  readonly mockup: MockupSummary;
}

export function MockupThumbnail({ mockup }: MockupThumbnailProps): ReactElement {
  const { t } = useTranslation();
  const { title, accent } = thumbnailTemplateOf(mockup);
  const tile = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = tile.current;
    if (element === null) return;
    if (accent === null) element.style.removeProperty("--pc");
    else element.style.setProperty("--pc", accent);
  }, [accent]);

  return (
    <div
      ref={tile}
      className={styles.thumbnail}
      role="img"
      aria-label={t("gallery.card.thumbnail", { title })}
      data-palette={accent === null ? "neutral" : "project"}
    >
      <div className={styles.top} />
      <div className={styles.side} />
      <div className={styles.main}>
        <div className={styles.block} />
        <div className={styles.columns}>
          {COLUMNS.map((column) => (
            <div key={column} className={styles.column} />
          ))}
        </div>
      </div>
      <span className={styles.name} aria-hidden="true">
        {title}
      </span>
    </div>
  );
}
